// @ts-strict-ignore
import { PureComponent } from 'react';
import '../css/missioncontrol.css';

import * as Sentry from '@sentry/react';

import Dashboard from './Dashboard';
import ErrorBoundary from './utils/ErrorBoundary';
import LoadingScreen from './utils/LoadingScreen';
import MenuDrawer from './MenuDrawer';
import { RogoThemeProvider } from './utils/RogoThemeProvider';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

import registerClasses from '../db';
import { initDatabase, loadAllTables } from '../db';
import { alertConfirm, alertError, alertInfo, alertSuccess, cancelConfirmTag } from '../alertDispatcher';
import PEI from '../PEI.json';
import {
  getCurrentSession,
  checkAllEngineeringPasswords,
  checkAllTuningPasswords,
  checkAllWeeklyTechPasswords,
} from '../dataModelHelpers';
import { AES } from 'crypto-js';
import { AccessLevelStore, SetStateAsync, isLocalhost, setStateAsync, wait } from '../utils';
import { migrateFastModeDefault, setLocalStorageExpiring } from '../db/local_storage';
import { AppBar, Portal } from '@mui/material';
import logger from '../logger';
import * as buffer from 'buffer';
import { RogoTheme, SettingsAccessLevel, ValidatePasswordFunction } from '../types/types';

import { currentUserIsAdmin } from '../db_ops/session_ops';
import { z } from 'zod';
import { _MS_PER_DAY } from '../constants';
import { isMobile } from 'react-device-detect';
import { saveRecoveryZip } from '../services/recovery-zip/RecoveryZipService';
import getVersion from '../version';
import { queryParams } from '../services/QueryParams';
import { loadBenchmarkDbFromS3 } from '../db/datamanager';
import dbStateLoader from '../db/DbStateLoader';
// regex for AWS keys pulled from https://aws.amazon.com/blogs/security/a-safer-way-to-distribute-aws-credentials-to-ec2/
const envVariables = z.object({
  // could just do z.string().startsWitb('pat'),
  VITE_AIRTABLE_API_KEY: z.string().regex(/pat[A-Za-z0-9]{14}\.[a-f0-9]{64}/),
  // could just do z.string().startsWitb('app'),
  VITE_AIRTABLE_BASE_ID: z.string().regex(/app[A-Za-z0-9]{14}/),
  VITE_EVENT_LOGGING: z.enum(['on', 'off']),
  VITE_SENTRY_DSN: z.string().startsWith('https://'),
  VITE_USE_RECOVERY_SERVER: z.enum(['true', 'false']),
});

envVariables.parse(import.meta.env);

declare global {
  namespace NodeJS {
    interface ProcessEnv extends z.infer<typeof envVariables> {}
  }
}

// Style overrides for the root <Grid>. Replaces the prior withStyles HOC.
const rootGridSx = {
  margin: 0,
  width: '100%',
} as const;

interface AppProps {}

export type LoadingAction = {
  action: (updater: (progress: number) => void) => Promise<any>;
  monitorProgress: boolean;
  onSuccess: (result: any) => void;
  onError: (err: Error) => void;
};

interface AppState {
  appBarHidden: boolean;
  buttonsRef: HTMLDivElement | undefined;
  camPermission: boolean;
  cipherString: string;
  dbInitialized: boolean;
  devMode: boolean;
  accessLevel: SettingsAccessLevel;
  loadingAction: LoadingAction | undefined;
  locPermission: boolean;
  offlineCount: number;
  sideBarText: string;
  sidebarToggleFunction: () => void;
  taskRef: HTMLDivElement | undefined;
  theme: RogoTheme;
  showHelpLinksPopup: boolean;
  passwordPromptOpen: boolean;
  validatePassword?: ValidatePasswordFunction;
  passwordLength: number;
  onPasswordClose?: (result: SettingsAccessLevel) => void;
}

class App extends PureComponent<AppProps, AppState> {
  POLL_ONLINE = 15000;
  MAX_POLL_TIMEOUT = 10000;
  MAX_OFFLINE_COUNT = 10;
  pollConnectionTimeout: NodeJS.Timeout | undefined;
  updateInterval: NodeJS.Timeout | undefined;
  loading: boolean;
  setStateAsync: SetStateAsync;
  setRecoveryDownloadFunction: any;
  disableSentry: boolean = false;

  constructor(props: AppProps) {
    super(props);
    const accessLevel = this.getAccessLevel();
    this.state = {
      appBarHidden: false,
      buttonsRef: undefined,
      camPermission: false,
      cipherString: '',
      dbInitialized: false,
      devMode: accessLevel === 'Technician',
      loadingAction: undefined,
      locPermission: false,
      offlineCount: 0,
      sideBarText: '',
      sidebarToggleFunction: () => {},
      taskRef: undefined,
      theme: (localStorage.getItem('theme') as RogoTheme) || 'light',
      showHelpLinksPopup: false,
      passwordPromptOpen: false,
      passwordLength: 0,
      validatePassword: undefined,
      onPasswordClose: undefined,
      accessLevel: accessLevel,
    };
    //this.pollConnectionTimeout = undefined;
    this.updateInterval = undefined;
    this.loading = false;
    this.startUpdateLoop = this.startUpdateLoop.bind(this);
    this.onLoading = this.onLoading.bind(this);
    this.updateApp = this.updateApp.bind(this);
    this.checkForUpdates = this.checkForUpdates.bind(this);
    this.generateUserLoginQr = this.generateUserLoginQr.bind(this);
    this.updateTaskRef = this.updateTaskRef.bind(this);
    this.updateAdditionalButtonsRef = this.updateAdditionalButtonsRef.bind(this);
    this.initRobotName = this.initRobotName.bind(this);
    this.setStateAsync = setStateAsync.bind(this);
    this.elevatePermissions = this.elevatePermissions.bind(this);
    window.Buffer = buffer.Buffer;

    // if we find 'localhost' in the window hostname, don't init sentry
    this.disableSentry = isLocalhost;
  }

  async updateApp(sw: ServiceWorker) {
    // notify of update
    const confirm = await alertConfirm(
      'A newer version is available. Would you like to update?',
      this.constructor.name,
    );

    if (confirm) {
      if (!isMobile) {
        await saveRecoveryZip(undefined, { tag: 'update' });
      }

      // Service Worker-based solution
      sw.postMessage({ type: 'SKIP_WAITING' });
      // TODO sleep here to wait for message deliver
      await wait(500);
      window.location.reload();
    }
  }

  async checkForUpdates() {
    if (navigator.serviceWorker) {
      await this.onLoading(async () => {
        const registration = await navigator.serviceWorker.ready;

        if (registration.waiting) {
          await this.updateApp(registration.waiting);

          return;
        }

        await registration.update();

        // After update(), a new worker may be installing — wait for it to become waiting
        if (registration.installing) {
          await new Promise<void>((resolve) => {
            registration.installing!.addEventListener('statechange', function handler() {
              if (registration.waiting) {
                registration.installing?.removeEventListener('statechange', handler);
                resolve();
              }
            });
          });
        }

        if (registration.waiting) {
          await this.updateApp(registration.waiting);
        } else {
          alertSuccess('App is up to date!');
        }
      });
    }
  }

  startUpdateLoop(registration: ServiceWorkerRegistration) {
    if (!this.updateInterval) {
      this.updateInterval = setInterval(
        () => {
          if (registration.waiting) {
            this.updateApp(registration.waiting);
          } else {
            registration.update();
          }
        },
        10 * 60 * 1000,
      ); // check for update every 10 minutes
    }
  }

  // loadAllTables() reads per-table data from MemoryBackup blobs. If that fails for
  // any reason — NotReadableError (backing file gone), SyntaxError (truncated JSON),
  // "No memory backup found", etc. — ask DbStateLoader for its stashed recovery-server
  // copy and retry once. Scope is naturally narrow: the stash only exists when
  // DbStateLoader actively chose local over a known-good server copy, so this catch
  // can't fire for unrelated bugs. The retry's own failure propagates uncaught so
  // genuine problems still surface.
  private async loadAllTablesWithRecoveryFallback() {
    try {
      await loadAllTables();
    } catch (err: any) {
      await logger.log('DB_INIT_FALLBACK', { stage: 'attempt', err });
      const fallback = await dbStateLoader.fallbackToRecoveryServer();

      if (!fallback) {
        await logger.log('DB_INIT_FALLBACK', { stage: 'unavailable' });
        throw err;
      }

      await logger.log('DB_INIT_FALLBACK', { stage: 'retry' });
      await loadAllTables();
    }
  }

  // initialize application database
  async initializeData() {
    registerClasses();
    let dataBackup: any;

    // For benchmarking performance
    const benchmarkFilename = queryParams.get('benchmark-filename');

    if (benchmarkFilename) {
      initDatabase();
      const res = await loadBenchmarkDbFromS3(benchmarkFilename);
      console.log('App.tsx - benchmark - load result', res);

      if (res) {
        this.setState({ dbInitialized: true });
        this.initRobotName();
      }

      return;
    }

    await this.onLoading(async () => {
      await logger.log('Initializing database');

      migrateFastModeDefault();

      const t1 = performance.now();
      initDatabase();
      const t2 = performance.now();
      await logger.log(`initializeData - Database initalized ${t2 - t1} ms`);
      console.log(`initializeData - Database initalized ${t2 - t1} ms`);

      dataBackup = await dbStateLoader.load(PEI);

      if (!dataBackup) {
        await logger.log('initializeData - No dataBackup returned');
        console.warn('initializeData - No dataBackup returned');

        return;
      }

      const t3 = performance.now();
      await logger.log(`initializeData - dataBackup fetched in ${t3 - t2} ms`);
      console.log(`initializeData - dataBackup fetched in ${t3 - t2} ms`);

      await this.loadAllTablesWithRecoveryFallback();
      const t4 = performance.now();
      await logger.log(`initializeData - Loaded table data in ${t4 - t3} ms`);
      console.log(`initializeData - Loaded table data in ${t4 - t3} ms`);
    });

    if (dataBackup) {
      this.setState({ dbInitialized: true });
      this.initRobotName();
    }
  }

  updateTaskRef(taskRef?: HTMLDivElement) {
    this.setState({ taskRef });
  }

  updateAdditionalButtonsRef(buttonsRef: HTMLDivElement) {
    this.setState({ buttonsRef });
  }

  /*
   * This function absracts the process of waiting for something to load. It
   * takes an async action function or promise as an argument and returns a
   * `Promise` which resolves or rejects with the results of the action
   * function.  If monitor progress is `true`, the action function will be
   * passed a function that takes one integer argument from 0-100. The value
   * will be used to determine the current progress of the operation. In this
   * way, long running processes can report progress to the user while running.
   */
  onLoading<T>(action: () => Promise<T>, monitorProgress = false): Promise<T> {
    if (!this.loading) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        this.setState({
          loadingAction: {
            // if a promise, convert to a function
            action,
            monitorProgress,
            onSuccess: (result) => {
              this.loading = false;
              resolve(result);
            },
            onError: (err) => {
              this.loading = false;
              reject(err);
            },
          },
        });
      });
    } else {
      return Promise.reject(new Error('Cannot start loading while already loading'));
    }
  }

  async initSentry() {
    if (this.disableSentry) {
      return;
    }

    // init sentry
    Sentry.init({
      release: getVersion(),
      dsn: import.meta.env.VITE_SENTRY_DSN,
      normalizeDepth: 10, // for sending Redux states
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [
            'https://.*.s3.amazonaws.com/', // all S3 requests
          ],
          networkRequestHeaders: ['X-Custom-Header'],
          networkResponseHeaders: ['X-Custom-Header'],
        }),
        // Disabling this for now because we are getting the following error:
        // Uncaught SecurityError: Failed to execute 'getImageData' on 'CanvasRenderingContext2D': The canvas has been tainted by cross-origin data
        // Sentry.replayCanvasIntegration(),
      ],
      ignoreErrors: [
        /AbortError: AbortError/,
        /WebSocket connection/,
        /AxiosError: Network Error/,
        /AxiosError: timeout exceeded/,
        /Failed to fetch/,
      ],
      // Every hash-route tab switch starts a "navigation" transaction (~80k/month), which
      // exhausted the org's Sentry transaction quota. Keep all pageloads, sample navigations.
      tracesSampler: ({ transactionContext }) => (transactionContext.op === 'navigation' ? 0.1 : 1.0),
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.1,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1.0,
    });

    //const origError = console.error;
    // console.error = function (...args) {

    //   if (args.length === 0) { throw new Error('Error while handling console.error()... at least one argument must be provided.') }

    //   let error: Error;
    //   let extras = {};

    //   // TODO need to review this, this was already here...
    //   if (args.length === 1 && args[0] instanceof Error) {
    //     // only an error was provided (most common)
    //     error = args[0];
    //   } else if (args.length === 2 && typeof args[0] === 'string' && args[1] instanceof Error) {
    //     // a comment was made on what caused the error (example: console.error('Could not refresh airtable jobs.', err);)
    //     error = args[1];
    //     error.message = `${args[0]} :: ${args[1].message}`;
    //   } else if (typeof args[0] === 'string') {
    //     // example: console.error('Could not export Shapefile', shp1, shp2, shp3);
    //     error = new Error(args[0]);
    //   } else {
    //     // if these are seen on Sentry, we should attempt to change it to one of the formats above
    //     error = new Error('Ill defined console.error message.');
    //   }

    //   for (const [i, arg] of args.entries()) {
    //     extras[`arg${i}`] = arg;
    //     if (arg instanceof Object) {
    //       // nested objects aren't unpacked, so try to stringify it
    //       try {
    //         extras[`arg${i}`] = JSON.stringify(arg);
    //       } catch (e) { }
    //     }
    //   }

    //   // Log console.error to sentry. Mark these so that we can rewrite to try/catch in the future.
    //   Sentry.withScope(scope => {
    //     scope.setContext('console.error arguments', extras);
    //     Sentry.captureException(error, {
    //       tags: {
    //         'from': 'console',
    //       },
    //     });
    //   });

    //   return origError(...args);
    // }
  }

  async initRobotName() {
    // clear robot from session
    const session = getCurrentSession();
    logger.setRobot();

    if (session) {
      session.robot_name = '';
    }
  }

  componentDidUpdate(_prevProps: AppProps, prevState: AppState) {
    localStorage.setItem('theme', this.state.theme);
    // TODO DEVMODE restriction
    // const devModeActive = !getLocalStorageDateExpired('devMode');
    // if (devModeActive !== this.state.devMode) {
    //   this.setState({ devMode: devModeActive });
    // }

    // we've been offline for too long
    if (prevState.offlineCount === this.MAX_OFFLINE_COUNT - 1 && this.state.offlineCount === this.MAX_OFFLINE_COUNT) {
      // alertWarn('Internet Offline');
    }
    // we've come back online
    else if (prevState.offlineCount >= this.MAX_OFFLINE_COUNT && this.state.offlineCount === 0) {
      // alertSuccess('Internet Online');
    }
  }

  componentDidMount() {
    // start update loop if service worker already registered
    if (navigator.serviceWorker) {
      // we specifically don't await this... it must not work normally because it holds up the whole app
      navigator.serviceWorker.ready.then(this.startUpdateLoop);
    }

    // Initialize everything asynchronously with proper error handling
    this.initializeApp().catch((err) => {
      console.error('App initialization failed:', err);
      Sentry.captureException(err);
      // Show user-friendly error or fallback UI
      alertError(
        'Application initialization failed. Please try refreshing the page or App Reset and if the problem persists, contact support.',
      );
    });
  }

  private async initializeApp() {
    // initialize Sentry logging
    try {
      await this.initSentry();
    } catch (err) {
      await logger.log('SENTRY_INIT_ERROR', { err });
      // Don't fail the entire app if Sentry fails
    }

    // initialize the DB
    try {
      await this.initializeData();
    } catch (err) {
      Sentry.captureException(err);
      await logger.log('DB_INIT_ERROR', { err });

      // TODO We really can't just continue on when this happens, but this really is also a show stopper
      // if we can't initalize the data, so we should really re-throw
      throw err;
    }
  }

  componentWillUnmount() {
    // cancel update timer
    clearInterval(this.updateInterval);
    clearTimeout(this.pollConnectionTimeout);
    cancelConfirmTag(this.constructor.name);
  }

  async generateUserLoginQr() {
    try {
      const session = getCurrentSession();

      if (!session) {
        throw Error('Session undefined');
      }

      const user = session.getUser();

      if (!user) {
        throw Error('Userr undefined');
      }

      const loginString = `${user.name}|${user.hashed_password}`;
      const aesPass = import.meta.env.VITE_AES_PASSPHRASE;
      const cipherString = AES.encrypt(loginString, aesPass).toString();
      await this.setStateAsync({ cipherString });
      const canvas = document.getElementById('loginQr') as HTMLCanvasElement;
      const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${user.airtable_name || user.name}QrLogin.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error(err);
      alertError('Failed to download QR code');
    }
  }

  /**
   *
   * @returns {SettingsAccessLevel} Get the access level based on the local storage saved values
   */
  getAccessLevel() {
    const { expires, accessLevel } = AccessLevelStore.get();

    if (expires < Date.now()) {
      AccessLevelStore.reset();

      return AccessLevelStore.get().accessLevel;
    }

    return accessLevel;
  }

  alertAccessLevelChanged(accessLevel: SettingsAccessLevel) {
    if (accessLevel === 'Technician' || accessLevel === 'Operator Tuning') {
      alertSuccess(`Logged in as ${accessLevel}`);
    } else {
      alertInfo(`Access level now set to Locked`);
    }
  }

  async elevatePermissions() {
    // Case 1: if admin, toggle on/off tech mode like normal
    if (currentUserIsAdmin(getCurrentSession())) {
      const accessLevel = this.state.accessLevel === 'Technician' ? 'Locked' : 'Technician';

      this.alertAccessLevelChanged(accessLevel);
      this.setState({
        devMode: !this.state.devMode,
        accessLevel,
      });

      AccessLevelStore.set({
        expires: Date.now() + 365 * 24 * 60 * 60 * 1000,
        accessLevel,
      });

      return;
    }

    // Case 2: if permissions are elevated, just lower to Locked
    // TODO for password feature
    if (this.state.accessLevel !== 'Locked') {
      alertInfo(`Access level now set to Locked`);

      this.alertAccessLevelChanged('Locked');
      this.setState({ accessLevel: 'Locked' });

      AccessLevelStore.set({
        expires: Date.now() + 365 * 24 * 60 * 60 * 1000,
        accessLevel: 'Locked',
      });

      return;
    }

    // Case 3: if permissions are locked, show password prompt
    // show password prompt
    this.setState({
      onPasswordClose: (result) => {
        this.alertAccessLevelChanged(result);
        this.setState({
          devMode: result === 'Technician',
          accessLevel: result,
          onPasswordClose: undefined,
          passwordPromptOpen: false,
        });

        if (result === 'Technician') {
          setLocalStorageExpiring('devMode', _MS_PER_DAY, 'start_of_day');
        }

        AccessLevelStore.set({
          expires: Date.now() + _MS_PER_DAY,
          accessLevel: result,
        });
      },
      passwordPromptOpen: true,
      validatePassword: async (password): Promise<SettingsAccessLevel> => {
        if (await checkAllTuningPasswords(password)) {
          return 'Operator Tuning';
        } else if (await checkAllEngineeringPasswords(password)) {
          return 'Technician';
        } else if (await checkAllWeeklyTechPasswords(password)) {
          return 'Technician';
        }

        return this.state.accessLevel;
      },
      passwordLength: 4,
    });
  }

  render() {
    const isDark = this.state.theme === 'dark';

    return (
      // RogoThemeProvider provides the v7 theme + CssBaseline + StyledEngineProvider.
      <RogoThemeProvider isDark={isDark}>
        <Container maxWidth={false} className="rootcontainer" disableGutters style={{ maxWidth: '100vw' }}>
          <Grid container spacing={1} justifyContent="center" sx={rootGridSx} size={12}>
            <Grid container spacing={1} style={{ flex: 1, display: 'flex' }} size={12}>
              <Portal
                container={document.getElementById(
                  this.state.dbInitialized ? 'menudrawer-container-1' : 'menudrawer-container-2',
                )}
              >
                <MenuDrawer
                  sideBarText={this.state.sideBarText}
                  darkMode={this.state.theme === 'dark'}
                  toggleTheme={() => {
                    this.setState({ theme: this.state.theme === 'dark' ? 'light' : 'dark' });
                  }}
                  devMode={this.state.accessLevel === 'Technician'}
                  elevatePermissions={this.elevatePermissions}
                  checkForUpdates={this.checkForUpdates}
                  onLoading={this.onLoading}
                  setToggleFunction={(fn) => this.setState({ sidebarToggleFunction: fn })}
                  generateUserLoginQr={this.generateUserLoginQr}
                  updateTaskRef={this.updateTaskRef}
                  updateAdditionalButtonsRef={this.updateAdditionalButtonsRef}
                  accessLevel={this.state.accessLevel}
                />
              </Portal>

              {!this.state.dbInitialized && (
                <AppBar position="static" color="default" id="menudrawer-container-2"></AppBar>
              )}

              <div id="activeAlerts" style={{ position: 'absolute', display: 'block', overflow: 'auto' }}></div>

              {this.state.dbInitialized ? (
                <ErrorBoundary>
                  <Dashboard
                    onLoading={this.onLoading}
                    devMode={this.state.devMode}
                    // TODO these props werent used in Dashboard...
                    // locPermission={this.state.locPermission}
                    // darkMode={!(this.state.theme === 'light')}
                    // setRecoveryDownloadFunction={this.setRecoveryDownloadFunction}
                    passwordPromptOpen={this.state.passwordPromptOpen}
                    validatePassword={this.state.validatePassword}
                    passwordLength={this.state.passwordLength}
                    onPasswordClose={this.state.onPasswordClose}
                    setSideBarText={(t: string) => {
                      this.setState({ sideBarText: t ? t.toString() : 'undefined' });
                    }}
                    hideAppBar={(h: boolean) => {
                      this.setState({ appBarHidden: h });
                    }}
                    appBarHidden={this.state.appBarHidden}
                    sidebarToggleFunction={this.state.sidebarToggleFunction}
                    taskRef={this.state.taskRef}
                    buttonsRef={this.state.buttonsRef}
                    disableSentry={this.disableSentry}
                    elevatePermissions={this.elevatePermissions}
                    accessLevel={this.state.accessLevel}
                  />
                </ErrorBoundary>
              ) : null}
            </Grid>
          </Grid>

          {/* loading screen */}
          <LoadingScreen action={this.state.loadingAction} />

          {/* 
          // We will disable this for now since it isn't used */}
          {/* {this.state.cipherString !== null && (
            <div style={{position: 'absolute'}} className={'hide'}>
              <QRCode
                id='loginQr'
                value={this.state.cipherString}
                size={100}
              />
            </div>
          )} */}
        </Container>
      </RogoThemeProvider>
    );
  }
}

export default App;
